import { auth } from "../firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  signInWithCustomToken,
} from "firebase/auth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleIcon, BackArrowIcon } from "../components/common/Icons";
import { isAdminEmail } from "../auth/adminEmails";
import { redeemPairCode } from "../api/alerts";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [code, setCode] = useState("");
  const navigate = useNavigate();

  // Signs this device in with a one-time code from a device that's already
  // signed in (More → Alerts). For the iPhone home-screen app, where Google's
  // pop-up sign-in can't open.
  async function codeLogin(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { token } = await redeemPairCode(code.trim());
      await signInWithCustomToken(auth, token);
      navigate("/admin", { replace: true });
    } catch (e2) {
      setErr(e2?.message || "That code didn't work.");
    } finally {
      setBusy(false);
    }
  }

  async function googleLogin() {
    setErr("");
    setBusy(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      if (!isAdminEmail(result.user.email)) {
        setErr(`Access denied. "${result?.user?.email || "(unknown)"}" isn't on the staff allowlist.`);
        await auth.signOut();
        return;
      }
      navigate("/admin", { replace: true });
    } catch (e) {
      console.error("Google login error:", e);
      setErr(e?.message || "Google login failed");
    } finally {
      setBusy(false);
    }
  }

  async function emailLogin(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await signInWithEmailAndPassword(auth, email.trim(), pw);
      if (!isAdminEmail(res.user.email)) {
        setErr(`Access denied. "${res?.user?.email || "(unknown)"}" isn't on the staff allowlist.`);
        await auth.signOut();
        return;
      }
      navigate("/admin", { replace: true });
    } catch (e) {
      console.error("Email login error:", e);
      setErr(e?.message || "Email login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-birdie-charcoal via-birdie-green-dark to-birdie-charcoal">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="bb-card p-8">
          <div className="text-center mb-8">
            <img
              src="/birdie-bus-logo.png"
              alt="Birdie Bus"
              className="mx-auto h-20 md:h-24 w-auto mb-5 drop-shadow"
            />
            <h1 className="font-heading text-2xl md:text-3xl text-birdie-green mb-2 tracking-wide">
              Admin Portal
            </h1>
            <p className="text-birdie-charcoal/70 font-body">Staff access only</p>
          </div>

          {err && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {err}
            </div>
          )}

          <button
            onClick={googleLogin}
            disabled={busy}
            className="w-full flex items-center justify-center gap-3 bg-birdie-green hover:bg-birdie-green-dark text-white rounded-xl py-3 px-4 font-body font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed mb-6"
          >
            <GoogleIcon className="w-5 h-5" />
            {busy ? "Signing in..." : "Sign in with Google"}
          </button>

          <div className="flex items-center mb-6">
            <div className="flex-1 border-t border-birdie-green/15"></div>
            <span className="px-4 text-sm text-birdie-warmgray font-body">or</span>
            <div className="flex-1 border-t border-birdie-green/15"></div>
          </div>

          <form onSubmit={emailLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-body font-semibold text-birdie-charcoal mb-2">
                Admin Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bb-input"
                placeholder="admin@birdiebus.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-body font-semibold text-birdie-charcoal mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                required
                className="bb-input"
                placeholder="Enter password"
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="bb-btn-primary w-full py-3 text-base"
            >
              {busy ? "Signing In..." : "Admin Sign In"}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-birdie-green/15">
            {showCode ? (
              <form onSubmit={codeLogin} className="space-y-3">
                <label htmlFor="pair-code" className="block text-sm font-body font-semibold text-birdie-charcoal">
                  Code from your other device
                </label>
                <input
                  id="pair-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  autoCapitalize="characters"
                  autoComplete="one-time-code"
                  className="bb-input font-mono tracking-widest text-center"
                  placeholder="ABCD-2345"
                  required
                />
                <button type="submit" disabled={busy} className="bb-btn-primary w-full py-3 text-base">
                  {busy ? "Signing In..." : "Sign in with code"}
                </button>
                <p className="text-xs text-birdie-charcoal/55 text-center">
                  Get one on a signed-in device: More → Alerts → Get a code.
                </p>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setShowCode(true)}
                className="w-full text-sm font-body font-semibold text-birdie-green hover:underline"
              >
                Use a code
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-birdie-cream/70 hover:text-birdie-mustard text-sm font-body hover:underline transition-colors"
          >
            <BackArrowIcon className="w-4 h-4" />
            Back to Customer Portal
          </button>
        </div>
      </div>
    </div>
  );
}
